import type { ReactNode } from 'react'
import type { LucideIcon, LucideProps } from 'lucide-react'
import {
  AppWindow,
  BadgeCheck,
  Boxes,
  Box,
  Building2,
  ClipboardList,
  Factory,
  FileCog,
  FlaskConical,
  Folder,
  FolderKanban,
  Globe,
  LayoutDashboard,
  LockKeyhole,
  PackageSearch,
  Printer,
  ScanLine,
  Settings2,
  ShieldCheck,
  Tags,
  Truck,
  UserRound,
  Wrench,
} from 'lucide-react'

export type AppIconName =
  | 'advanced'
  | 'admin'
  | 'app'
  | 'auth'
  | 'box'
  | 'browser'
  | 'contractor'
  | 'dashboard'
  | 'document'
  | 'factory'
  | 'fields'
  | 'folder'
  | 'infrastructure'
  | 'labels'
  | 'nomenclature'
  | 'orders'
  | 'pallet'
  | 'preview'
  | 'print'
  | 'production'
  | 'profiles'
  | 'queue'
  | 'reports'
  | 'sandbox'
  | 'scan'
  | 'settings'
  | 'shipments'
  | 'tasks'
  | 'worker'

const ICON_MAP: Record<AppIconName, LucideIcon> = {
  advanced: Wrench,
  admin: BadgeCheck,
  app: Boxes,
  auth: LockKeyhole,
  box: Box,
  browser: Globe,
  contractor: Building2,
  dashboard: LayoutDashboard,
  document: FileCog,
  factory: Factory,
  fields: ClipboardList,
  folder: Folder,
  infrastructure: AppWindow,
  labels: Tags,
  nomenclature: ClipboardList,
  orders: FolderKanban,
  pallet: Truck,
  preview: PackageSearch,
  print: Printer,
  production: Factory,
  profiles: ClipboardList,
  queue: ClipboardList,
  reports: ShieldCheck,
  sandbox: FlaskConical,
  scan: ScanLine,
  settings: Settings2,
  shipments: Truck,
  tasks: Boxes,
  worker: UserRound,
}

export function AppIcon({ name, className, strokeWidth = 1.9, ...props }: LucideProps & { name: AppIconName }) {
  const Icon = ICON_MAP[name]
  return <Icon className={className} strokeWidth={strokeWidth} {...props} />
}

export function IconLabel({
  icon,
  children,
  className = 'inline-flex items-center gap-2',
  iconClassName = 'h-4 w-4',
}: {
  icon: AppIconName
  children: ReactNode
  className?: string
  iconClassName?: string
}) {
  return (
    <span className={className}>
      <AppIcon name={icon} className={iconClassName} aria-hidden="true" />
      <span>{children}</span>
    </span>
  )
}

export function SidebarApiIcon({ icon, label, className = 'h-4 w-4' }: { icon?: string; label?: string; className?: string }) {
  const trimmed = (icon ?? '').trim()

  const name: AppIconName =
    trimmed === '\u{1F4CA}' ? 'dashboard' :
    trimmed === '\u270F\uFE0F' ? 'document' :
    trimmed === '\u{1F4E6}' ? 'tasks' :
    trimmed === '\u2699\uFE0F' ? 'settings' :
    trimmed === '\u{1F527}' ? 'advanced' :
    trimmed === '\u{1F5C2}\uFE0F' ? 'orders' :
    trimmed === '\u2705' ? 'reports' :
    trimmed === '\u{1F69B}' || trimmed === '�\u{1F69A}' ? 'shipments' :
    label?.toLowerCase().includes('ка') ? 'box' :
    'tasks'

  return <AppIcon name={name} className={className} aria-hidden="true" />
}